/**
 * AgentIcon component - Unified agent avatar/icon display
 * Handles images, emojis, and fallback initials for consistent agent representation
 */

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useState } from "react";
import {
  Bot,
  Brain,
  Building,
  Code,
  Globe,
  Headphones,
  Heart,
  Lightbulb,
  Palette,
  Rocket,
  Settings,
  Shield,
  Sparkles,
  Star,
  User,
  Zap,
  type LucideIcon,
} from "lucide-react";

/**
 * Predefined avatar options matching avatar-selector.tsx
 * Maps icon IDs to their corresponding Lucide icons and background colors
 */
const PREDEFINED_AVATARS: Record<string, { icon: LucideIcon; color: string }> = {
  bot: { icon: Bot, color: "bg-blue-500" },
  user: { icon: User, color: "bg-gray-500" },
  zap: { icon: Zap, color: "bg-yellow-500" },
  brain: { icon: Brain, color: "bg-purple-500" },
  heart: { icon: Heart, color: "bg-red-500" },
  shield: { icon: Shield, color: "bg-green-500" },
  star: { icon: Star, color: "bg-orange-500" },
  sparkles: { icon: Sparkles, color: "bg-pink-500" },
  rocket: { icon: Rocket, color: "bg-indigo-500" },
  lightbulb: { icon: Lightbulb, color: "bg-amber-500" },
  settings: { icon: Settings, color: "bg-slate-500" },
  globe: { icon: Globe, color: "bg-teal-500" },
  building: { icon: Building, color: "bg-stone-600" },
  headphones: { icon: Headphones, color: "bg-cyan-500" },
  code: { icon: Code, color: "bg-emerald-500" },
  palette: { icon: Palette, color: "bg-rose-500" },
};

/**
 * Size variants for the AgentIcon component
 * Maps to specific dimensions for consistent sizing across the application
 */
type AgentIconSize = "xs" | "sm" | "md" | "lg" | "xl";

/**
 * Size configuration mapping for avatar dimensions
 * Provides Tailwind classes for each size variant
 */
const SIZE_CLASSES: Record<AgentIconSize, { container: string; text: string; icon: string }> = {
  xs: { container: "size-5 min-h-5 min-w-5", text: "text-[11px] leading-none", icon: "size-3" },
  sm: { container: "size-6 min-h-6 min-w-6", text: "text-[11px] leading-none", icon: "size-3" },
  md: { container: "size-8", text: "text-[11px] leading-none", icon: "size-4" },
  lg: { container: "size-10", text: "text-[13px] leading-none", icon: "size-5" },
  xl: { container: "size-12", text: "text-[15px] leading-none", icon: "size-6" },
};

/**
 * Props for the AgentIcon component
 * Supports both full agent objects and individual properties
 */
interface AgentIconProps {
  /**
   * Agent avatar URL, base64 string, or emoji
   * Can be undefined/null for fallback to initials
   */
  avatar?: string | null;
  /**
   * Agent name for generating fallback initials
   * Required for accessibility and fallback display
   */
  name: string;
  /**
   * Size variant for the icon
   * Defaults to 'md' (32px)
   */
  size?: AgentIconSize;
  /**
   * Optional custom class name for additional styling
   * Applied to the root Avatar component
   */
  className?: string;
  /**
   * Border radius variant for the avatar
   * Defaults to 'rounded-lg'
   */
  rounded?: "full" | "lg" | "xl" | "md" | "sm" | "none";
  /**
   * Whether to show a loading skeleton
   * Useful when agent data is being fetched
   */
  loading?: boolean;
  /**
   * Whether to render the avatar in a muted neutral treatment.
   * Useful for dense UI surfaces like history lists.
   */
  muted?: boolean;
}

/**
 * Extracts initials from a name string
 * Returns up to 2 uppercase characters for avatar fallback display
 *
 * @param {string} name - The name to extract initials from
 * @returns {string} Uppercase initials (max 2 characters)
 */
function getInitials(name: string): string {
  if (!name) return "TA";

  const validWords = name
    .split(" ")
    .filter((word) => /^[a-zA-Z]/.test(word));

  if (validWords.length >= 2) {
    return validWords
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }

  const condensedName = name.replace(/[^a-zA-Z0-9]/g, "");
  return condensedName.slice(0, 2).toUpperCase() || "TA";
}

/**
 * Checks if a string is an emoji
 * Uses regex to detect emoji characters
 *
 * @param {string} str - The string to check
 * @returns {boolean} True if the string is an emoji
 */
function isEmoji(str: string): boolean {
  if (!str || str.length > 4) return false;
  const emojiRegex = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}]$/u;
  return emojiRegex.test(str);
}

/**
 * Checks if a string is a valid image source
 * Supports URLs, data URIs, and relative paths
 *
 * @param {string} str - The string to check
 * @returns {boolean} True if the string is a valid image source
 */
function isImageSource(str: string): boolean {
  if (!str) return false;
  return (
    str.startsWith("http://") ||
    str.startsWith("https://") ||
    str.startsWith("data:") ||
    str.startsWith("/")
  );
}

/**
 * Checks if a string is a predefined avatar icon ID
 *
 * @param {string} str - The string to check
 * @returns {boolean} True if the string matches a predefined avatar ID
 */
function isPredefinedAvatar(str: string): boolean {
  if (!str) return false;
  return str.toLowerCase() in PREDEFINED_AVATARS;
}

/**
 * Gets the rounded class based on the variant
 *
 * @param {string} rounded - The rounded variant
 * @returns {string} Tailwind class for border radius
 */
function getRoundedClass(rounded: AgentIconProps["rounded"]): string {
  switch (rounded) {
    case "full":
      return "rounded-full";
    case "xl":
      return "rounded-xl";
    case "lg":
      return "rounded-lg";
    case "md":
      return "rounded-md";
    case "sm":
      return "rounded-sm";
    case "none":
      return "rounded-none";
    default:
      return "rounded-lg";
  }
}

/**
 * AgentIcon component
 * Unified display for agent avatars supporting images, emojis, and fallback initials
 *
 * @param {AgentIconProps} props - Component props
 * @returns {JSX.Element} The rendered agent icon
 *
 * @example
 * // With image URL
 * <AgentIcon avatar="https://example.com/avatar.png" name="Sales Agent" size="md" />
 *
 * @example
 * // With emoji
 * <AgentIcon avatar="🤖" name="Bot Agent" size="lg" />
 *
 * @example
 * // Fallback to initials
 * <AgentIcon name="Customer Support" size="sm" />
 */
export function AgentIcon({
  avatar,
  name,
  size = "md",
  className,
  rounded = "lg",
  loading = false,
  muted = false,
}: AgentIconProps) {
  const sizeConfig = SIZE_CLASSES[size];
  const roundedClass = getRoundedClass(rounded);
  const mutedClasses =
    "bg-muted/80 text-foreground/75 ring-1 ring-border/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]";

  // Image avatars are network-fetched (signed S3 URLs), so they arrive a beat
  // after the row renders. Track the load status to show an animated pulse in
  // that gap instead of flashing the initials, then fall back to initials only
  // if the image genuinely fails.
  const [imgStatus, setImgStatus] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");

  /**
   * Loading state - show skeleton placeholder
   */
  if (loading) {
    return (
      <div
        className={cn(
          sizeConfig.container,
          roundedClass,
          "bg-muted animate-pulse flex-shrink-0",
          className
        )}
      />
    );
  }

  /**
   * Predefined avatar - display Lucide icon with background color
   */
  if (avatar && isPredefinedAvatar(avatar)) {
    const avatarConfig = PREDEFINED_AVATARS[avatar.toLowerCase()];
    const IconComponent = avatarConfig.icon;
    return (
      <div
        className={cn(
          sizeConfig.container,
          roundedClass,
          muted ? mutedClasses : avatarConfig.color,
          "flex items-center justify-center flex-shrink-0",
          className
        )}
      >
        <IconComponent
          className={cn(sizeConfig.icon, muted ? "text-muted-foreground" : "text-white")}
        />
      </div>
    );
  }

  /**
   * Emoji avatar - display emoji directly
   */
  if (avatar && isEmoji(avatar)) {
    return (
      <div
        className={cn(
          sizeConfig.container,
          roundedClass,
          muted
            ? `flex items-center justify-center ${mutedClasses} flex-shrink-0 grayscale`
            : "flex items-center justify-center bg-primary/10 flex-shrink-0",
          className
        )}
      >
        <span className={sizeConfig.text}>{avatar}</span>
      </div>
    );
  }

  /**
   * Image avatar - use Avatar component with fallback
   */
  if (avatar && isImageSource(avatar)) {
    return (
      <Avatar
        className={cn(
          sizeConfig.container,
          roundedClass,
          "flex-shrink-0",
          muted && `${mutedClasses} grayscale`,
          className,
        )}
      >
        <AvatarImage
          src={avatar}
          alt={name}
          className={cn(roundedClass, muted && "opacity-80")}
          onLoadingStatusChange={setImgStatus}
        />
        <AvatarFallback
          className={cn(
            roundedClass,
            // While the image is still loading, show a neutral pulse rather
            // than the coloured initials so the swap doesn't read as a flash.
            imgStatus === "error"
              ? muted
                ? `${mutedClasses} font-semibold tracking-tight`
                : "bg-primary text-primary-foreground font-semibold tracking-tight"
              : "bg-transparent",
            sizeConfig.text
          )}
        >
          {imgStatus === "error" ? (
            getInitials(name)
          ) : (
            <span
              className={cn("block h-full w-full bg-muted animate-pulse", roundedClass)}
              aria-label="Loading avatar"
            />
          )}
        </AvatarFallback>
      </Avatar>
    );
  }

  /**
   * Fallback - show initials or bot icon
   */
  return (
    <Avatar className={cn(sizeConfig.container, roundedClass, "flex-shrink-0", className)}>
      <AvatarFallback
        className={cn(
          roundedClass,
          muted
            ? `${mutedClasses} font-semibold tracking-tight`
            : "bg-primary text-primary-foreground font-semibold tracking-tight",
          sizeConfig.text
        )}
      >
        {name ? getInitials(name) : <Bot className={sizeConfig.icon} />}
      </AvatarFallback>
    </Avatar>
  );
}

export type { AgentIconProps, AgentIconSize };
